import * as React from 'react'

import { Alert, Box, Button, Icon, Text } from '@factorialco/deprecated-design-system'
import { FormikProvider } from 'formik'

import Modal from 'modules/core/components/Modal'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EvaluationFormEditor } from 'modules/ats/components/EvaluationForm/components/EvaluationFormEditor'
import {
  buildEmptyQuestion,
  useEvaluationFormForm,
} from 'modules/ats/components/EvaluationForm/useEvaluationFormForm'

import { useCreateEvaluationFormTemplate } from './useCreateEvaluationFormTemplate'

interface Props {
  onClose: () => void
  evaluationFormId?: number
  duplicate?: boolean
}

export const CreateEvaluationFormTemplateSidepanel = ({
  onClose,
  evaluationFormId,
  duplicate,
}: Props) => {
  const session = useCurrentSession()
  const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] = React.useState(false)
  const { create } = useCreateEvaluationFormTemplate()

  const { form, hasInvalidQuestions } = useEvaluationFormForm({
    defaultValues: {
      name: '',
      questions: [buildEmptyQuestion()],
      duplicate: duplicate ?? false,
    },
    async onSubmit(data, { setErrors }) {
      const result = await create({
        companyId: session.company.id,
        name: data.name,
        questions: data.questions.map((question) => ({
          id: question.id,
          description: question.description,
          text: question.text,
        })),
      })
      const handled = handleResult(result, setErrors)

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('ats.evaluation_forms.new.success_message'),
        })
        onClose()
      }

      return handled
    },
  })

  const onCancelClicked = () => {
    form.dirty ? setUnsavedChangesModalOpen(true) : onClose()
  }

  return (
    <FormikProvider value={form}>
      <Sidepanel
        open
        width="wide"
        title={i18n.t('ats.evaluation_forms.new.title')}
        footer={
          <Box flexDirection="row" justifyContent="spaceBetween" width="full" alignItems="center">
            {form.dirty ? (
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon="alert-circle-line" color="grey800" />
                <Text.Normal size="200">{i18n.t('ats.evaluation_forms.form.warning')}</Text.Normal>
              </Box>
            ) : (
              <div />
            )}

            <Box flexDirection="row" gap="s8" alignSelf="flexEnd">
              <Button
                onClick={onCancelClicked}
                type="secondary"
                aria-label={i18n.t('ats.evaluation_forms.form.cancel')}
              >
                {i18n.t('common.cancel')}
              </Button>
              <Button
                onClick={() => form.handleSubmit()}
                aria-label={i18n.t('ats.evaluation_forms.form.save')}
                disabled={form.isSubmitting}
              >
                {i18n.t('common.save')}
              </Button>
            </Box>
          </Box>
        }
        onClose={onClose}
        closeLabel={i18n.t('common.close')}
      >
        <Box gap="s16">
          <Alert>{i18n.t('ats.evaluation_forms.new.info')}</Alert>
          <EvaluationFormEditor
            evaluationFormId={evaluationFormId}
            hasInvalidQuestions={hasInvalidQuestions}
          />
        </Box>

        {unsavedChangesModalOpen && (
          <Modal
            illustration="alert"
            title={i18n.t('ats.evaluation_forms.form.unsaved_changes.title')}
            description={i18n.t('ats.evaluation_forms.form.unsaved_changes.description')}
            onClose={() => setUnsavedChangesModalOpen(false)}
            onSubmit={onClose}
            footer={
              <Box flexDirection="row" gap="s16">
                <Button type="negative" submit>
                  {i18n.t('ats.evaluation_forms.form.unsaved_changes.cancel_and_delete')}
                </Button>
                <Button onClick={() => setUnsavedChangesModalOpen(false)}>
                  {i18n.t('ats.evaluation_forms.form.unsaved_changes.continue')}
                </Button>
              </Box>
            }
          />
        )}
      </Sidepanel>
    </FormikProvider>
  )
}
